import { Meal } from 'fitbeat.models';
import { IMealRepository } from '../interfaces/imeal-repository';

export class DbMealRepository implements IMealRepository {
    public approvedOnlyFilter = {
        approveStatus: 'Approved',
    };
    constructor(private mealDb: any) { }
    public async add(meal: Meal): Promise<Meal> {
        const doc = JSON.parse(JSON.stringify(meal));
        doc._id = meal._id;
        await this.mealDb.put(doc);
        return meal;
    }

    public async get(uid: string): Promise<Meal | undefined> {
        try {
            const meal = await this.mealDb.get(uid);
            return Object.assign(new Meal(), meal);
        } catch (err) {
            if (err.status === 404) {
                return undefined;
            } else {
                throw err;
            }
        }
    }

    public async update(meal: Meal): Promise<Meal> {
        const doc = JSON.parse(JSON.stringify(meal));
        await this.mealDb.put(doc);
        return meal;
    }

    public async getAll(): Promise<Meal[]> {
        const results = await this.mealDb.find({
            limit: Number.MAX_SAFE_INTEGER,
            selector: {
                _id: {
                    $ne: '0',
                },
                ...this.approvedOnlyFilter,
            },
        });
        const docs = results.docs;
        const meals: Meal[] = docs.map((doc: any): Meal => {
            const returnObj: Meal = Object.assign(new Meal(), doc);
            return returnObj;
        });

        return meals;
    }

    public async getImage(uid: string): Promise<string> {
        const doc = await this.mealDb.get(uid, {
            attachments: true,
        });
        const attachment: any = doc._attachments ? doc._attachments[`${doc._id}-full.png`] : null;
        return attachment ? attachment.data : '';
    }

    public async setImage(uid: string, base64Image: string): Promise<boolean> {
        const doc = await this.mealDb.get(uid);
        await this.mealDb.putAttachment(uid, `${doc._id}-full.png`, doc._rev,
            Buffer.from(base64Image, 'base64'), 'image/png');
        return true;
    }

    public async getThumbnail(uid: string): Promise<string> {
        const doc = await this.mealDb.get(uid, { attachments: true });
        const attachmentThumbnail: any = doc._attachments ? doc._attachments[`${doc._id}-thumbnail.png`] : null;
        const thumbnail = attachmentThumbnail ? attachmentThumbnail.data : '';

        return thumbnail;
    }

    public async setThumbnail(uid: string, base64Image: string): Promise<boolean> {
        const doc = await this.mealDb.get(uid);
        await this.mealDb.putAttachment(uid, `${doc._id}-thumbnail.png`, doc._rev,
            Buffer.from(base64Image, 'base64'), 'image/png');
        return true;
    }

    public async getMealsForMealPlan(mealPlanId: string): Promise<Meal[]> {
        const result = await this.mealDb.find({
            selector: {
                mealPlanId,
                ...this.approvedOnlyFilter,
            },
        });
        const docs = result.docs;

        const meals: Meal[] = [];
        for (const doc of docs) {
            const returnObj: Meal = Object.assign(new Meal(), doc);

            if (doc._attachments && doc._attachments[`${ doc._id}-thumbnail.png`]) {
                const thumbnailBuffer = await this.mealDb.getAttachment(doc._id, `${doc._id}-thumbnail.png`);
                const thumbnail = Buffer.from(thumbnailBuffer).toString('base64');
                returnObj.thumbnail = thumbnail;
            }

            if (doc._attachments && doc._attachments[`${doc._id}-full.png`]) {
                const imageBuffer = await this.mealDb.getAttachment(doc._id, `${doc._id}-full.png`);
                const image = Buffer.from(imageBuffer).toString('base64');
                returnObj.image = image;
            }

            meals.push(returnObj);
        }
        return meals;
    }

    public async getAllMealsForTimeOfDay(
        timeOfDay: string,
        gender: string,
        dietGoal: string,
        foodPreference: string,
        organisationMealTag: string,
    ): Promise<Meal[]> {
      const meals: Meal[] = [];
      const selector: any = {};
      if (organisationMealTag) {
        selector.$and = [
          { timeOfMeal: { $in: [timeOfDay] } },
          { foodPreference: { $in: [foodPreference] } },
          { gender },
          { dietGoal },
          { ...this.approvedOnlyFilter },
          {
            $or: [
              { organisation: organisationMealTag },
              { organisation: { $exists: false } },
            ],
          },
          {
            $or: [
              { excludedOrganisations: { $nin: [organisationMealTag] } },
              { excludedOrganisations: { $exists: false } },
            ],
          },
        ];
      } else {
        selector.timeOfMeal = { $in: [timeOfDay] };
        selector.foodPreference = { $in: [foodPreference] };
        selector.gender = gender;
        selector.dietGoal = dietGoal;
        Object.assign(selector, this.approvedOnlyFilter);
        selector.organisation = { $exists: false };
      }
      const result = await this.mealDb.find({
        selector,
        limit: 10000,
      });
      const docs = result.docs;
      for (const doc of docs) {
        const returnObj: Meal = Object.assign(new Meal(), doc);
        meals.push(returnObj);
      }
      return meals;
    }

    public async getThumbnails(mealPlanId: string): Promise<any> {
        const result = await this.mealDb.find({
            selector: {
                mealPlanId,
                _attachments: {
                    $exists: true,
                },
                ...this.approvedOnlyFilter,
            },
        });
        const docs = result.docs;
        const mealsWithThumbnails = docs.filter((doc: any) =>
            !!doc._attachments[`${doc._id}-thumbnail.png`]);

        const thumbnails = {};
        const promises = [];

        for (const meal of mealsWithThumbnails) {
            promises.push(this.mealDb.getAttachment(meal._id, `${meal._id}-thumbnail.png`));
        }

        const thumbnailsAttachments = await Promise.all(promises);
        thumbnailsAttachments.forEach((attachment, index) => {
            const thumbnail = Buffer.from(attachment).toString('base64');
            thumbnails[docs[index]._id] = thumbnail;
        });

        return thumbnails;
    }
}
